import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Input,
  List,
  ListItem,
  Text,
  useOutsideClick,
  InputGroup,
  InputRightElement,
  Icon,
} from '@chakra-ui/react';
import { RiArrowDownSLine, RiCloseLine, RiSearchLine } from 'react-icons/ri';

import { IUserOption } from '../../../services/usersService';

interface UserSelectProps {
  users: IUserOption[];
  value: string;
  onChange: (userId: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

export function UserSelect({
  users,
  value,
  onChange,
  placeholder = 'Selecione um diretor',
  isDisabled,
}: UserSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: containerRef,
    handler: () => setIsOpen(false),
  });

  // Quando o valor muda externamente, limpar busca
  useEffect(() => {
    if (!isOpen) setSearch('');
  }, [isOpen]);

  const selectedUser = users.find(u => u.id === value);

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const term = search.toLowerCase();
    return users.filter(
      u =>
        u.name.toLowerCase().includes(term) ||
        (u.email && u.email.toLowerCase().includes(term)) ||
        (u.nickname && u.nickname.toLowerCase().includes(term)),
    );
  }, [users, search]);

  const handleSelect = (userId: string) => {
    onChange(userId);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  return (
    <Box position="relative" ref={containerRef} w="100%">
      <InputGroup>
        <Input
          value={isOpen ? search : selectedUser?.name || ''}
          onChange={e => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          isDisabled={isDisabled}
          color="#1a202c"
          bg="white"
          borderColor="gray.300"
          _hover={{ borderColor: 'gray.400' }}
          _placeholder={{ color: 'gray.400' }}
          autoComplete="off"
        />
        <InputRightElement>
          {value && !isOpen ? (
            <Icon
              as={RiCloseLine}
              color="gray.500"
              cursor="pointer"
              onClick={handleClear}
            />
          ) : (
            <Icon
              as={isOpen ? RiSearchLine : RiArrowDownSLine}
              color="gray.500"
              cursor="pointer"
              onClick={() => setIsOpen(!isOpen)}
            />
          )}
        </InputRightElement>
      </InputGroup>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg="white"
          borderWidth={1}
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          maxH="240px"
          overflowY="auto"
          zIndex={10}
        >
          {filteredUsers.length === 0 ? (
            <Box p={3}>
              <Text fontSize="sm" color="gray.500">
                Nenhum usuário encontrado
              </Text>
            </Box>
          ) : (
            <List spacing={0}>
              {filteredUsers.map(user => (
                <ListItem
                  key={user.id}
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={user.id === value ? 'blue.50' : 'white'}
                  _hover={{ bg: 'blue.50' }}
                  onClick={() => handleSelect(user.id)}
                >
                  <Text
                    fontSize="sm"
                    fontWeight={user.id === value ? 'bold' : 'normal'}
                    color="#1a202c"
                  >
                    {user.name}
                  </Text>
                  {user.email && (
                    <Text fontSize="xs" color="gray.500">
                      {user.email}
                    </Text>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
}
